import React, { createRef } from 'react';
import cn from 'classnames';
import type { ITimeZone } from '@dealroadshow/uikit-date-time';
import {
  EMPTY_CELL_STATE,
  TRACE_TABLE_MATURITY_DATE_FORMAT,
  TRACE_TABLE_TRADE_FORMAT,
} from '@/creditflowresearch/ui/common/constants';
import { BodyCellPure } from '@creditflowresearch/uilib/Table/DataTable/Body/BodyCell';
import { BondNameCell, getDateCell } from '@/creditflowresearch/ui/common/components/TableCells';
import { formatRecordTimestamp } from '@/creditflowresearch/application/helpers/recordTimestamp';
import {
  getBaseCell,
  getRNUMCell,
  getReportingPartySideCell,
  getCPTCell,
  getTradeStringSizeCell,
  getNumberWithDecimalsCell,
  getSectorCell,
} from './cellCallbacks';
import { getIdentifierCell } from '@/creditflowresearch/ui/components/bonds/common/BondTables/cellCallbacks';

import alignStyles from '@creditflowresearch/uilib/styles/helpers/align.module.scss';
import styles from '@/creditflowresearch/ui/components/bonds/common/BondTables/bondsTables.module.scss';
import spacesStyles from '@creditflowresearch/uilib/styles/helpers/spaces.module.scss';

interface IProps {
  rowIndex: number,
  row: Record<string, any>,
  timeZone: ITimeZone,
}

const TradesRow = (props: IProps) => {
  const { row, timeZone } = props;
  const rowRef = createRef<HTMLElement>();

  const handleMouseEnter = () => {
    const node = rowRef.current;

    if (node) {
      (node.querySelector('.togglerCheckbox') as HTMLInputElement).checked = false;
    }
  };

  return (
    <tr
      // @ts-ignore
      ref={ rowRef }
      onMouseEnter={ handleMouseEnter }
      className={ styles.row }
    >
      <BodyCellPure
        name="sector"
        className={ alignStyles.alignLeft }
        rowIndex={ props.rowIndex }
        rowData={ row }
        cellCallback={ getSectorCell }
      />
      <BodyCellPure
        name="name"
        className={ alignStyles.alignLeft }
        rowIndex={ props.rowIndex }
        rowData={ row }
        cellCallback={ BondNameCell }
      />
      <BodyCellPure
        name="stringTradeSize"
        className={ alignStyles.alignRight }
        rowIndex={ props.rowIndex }
        rowData={ row }
        cellCallback={ getTradeStringSizeCell }
      />
      <BodyCellPure
        name="price"
        className={ alignStyles.alignRight }
        rowIndex={ props.rowIndex }
        rowData={ row }
        cellCallback={ (props) => getNumberWithDecimalsCell(props, 5) }
      />
      <BodyCellPure
        name="coupon"
        className={ alignStyles.alignRight }
        rowIndex={ props.rowIndex }
        rowData={ row }
        cellCallback={ (props) => getNumberWithDecimalsCell(props, 2) }
      />
      <BodyCellPure
        name="maturityDate"
        className={ alignStyles.alignRight }
        rowIndex={ props.rowIndex }
        rowData={ row }
        cellCallback={ (cellData) => getDateCell(cellData, TRACE_TABLE_MATURITY_DATE_FORMAT) }
      />
      <BodyCellPure
        name="factor"
        className={ alignStyles.alignRight }
        rowIndex={ props.rowIndex }
        rowData={ row }
        cellCallback={ (props) => getNumberWithDecimalsCell(props, 2) }
      />
      <BodyCellPure
        name="remuneration"
        className={ alignStyles.alignCenter }
        rowIndex={ props.rowIndex }
        rowData={ row }
        cellCallback={ getRNUMCell }
      />
      <BodyCellPure
        name="tradeStatus"
        className={ alignStyles.alignCenter }
        rowIndex={ props.rowIndex }
        rowData={ row }
        cellCallback={ getBaseCell }
      />
      <BodyCellPure
        name="reportingPartyType"
        className={ alignStyles.alignCenter }
        rowIndex={ props.rowIndex }
        rowData={ row }
        cellCallback={ getBaseCell }
      />
      <BodyCellPure
        name="reportingPartySide"
        className={ alignStyles.alignCenter }
        rowIndex={ props.rowIndex }
        rowData={ row }
        cellCallback={ getReportingPartySideCell }
      />
      <BodyCellPure
        name="contraPartyType"
        className={ alignStyles.alignRight }
        rowIndex={ props.rowIndex }
        rowData={ row }
        cellCallback={ getCPTCell }
      />
      <BodyCellPure
        name="identifier"
        className={
          cn(styles.identifierCell, alignStyles.alignRight, spacesStyles.ptn, spacesStyles.pbn, spacesStyles.prl)
        }
        rowIndex={ props.rowIndex }
        rowData={ row }
        cellCallback={ getIdentifierCell }
      />
      <BodyCellPure
        name="tradeDate"
        className={ cn(styles.dueByCell, alignStyles.alignRight) }
        rowIndex={ props.rowIndex }
        rowData={ row }
        cellCallback={ ({ cellData }) => (
          formatRecordTimestamp(cellData, TRACE_TABLE_TRADE_FORMAT, timeZone) ?? EMPTY_CELL_STATE
        ) }
      />
    </tr>
  );
};

export default TradesRow;
